import { Card, CardBody, CardHeader, CardHeading, Empty, FormattedCurrency } from "@dgs/core";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import { EventBudgetAnalyticsCostResource, EventBudgetAnalyticsCostsByCategoryResource } from "../analyticsTypes";
import { useBudgetAnalyticsGridData } from "./useBudgetAnalyticsGridData";

const TableCardBody = styled(CardBody)<{ $columnCount: number }>`
	display: grid;
	grid-template-columns: 200px repeat(${({ $columnCount }) => $columnCount}, 1fr);
	overflow: auto;
	padding: 0px;
`;

const Cell = styled.div<{ $isFirstCell?: boolean; $isLastRow?: boolean }>`
	display: flex;
	align-items: center;
	padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
	${({ $isLastRow }) =>
		$isLastRow
			? ""
			: css`
					border-bottom: 1px solid ${({ theme }) => theme.colors.card.background.border};
				`}

	${({ $isFirstCell }) =>
		$isFirstCell
			? ""
			: css`
					border-left: 1px solid ${({ theme }) => theme.colors.card.background.border};
				`}
`;

const BoldCell = styled(Cell)`
	font-weight: ${({ theme }) => theme.fonts.weights.bold};
`;

const EmptyCardBody = styled(CardBody)`
	display: flex;
	justify-content: center;
	align-items: center;
	height: 300px;
`;

interface Props {
	acceptedUserRequestCosts: EventBudgetAnalyticsCostResource[];
	contactCosts: EventBudgetAnalyticsCostResource[];
	costsByCategory: EventBudgetAnalyticsCostsByCategoryResource[];
}

export function CostsByCategories({ acceptedUserRequestCosts, costsByCategory, contactCosts }: Props) {
	const { columns, rows } = useBudgetAnalyticsGridData(acceptedUserRequestCosts, costsByCategory, contactCosts);
	const { t } = useTranslation();

	if (!columns.length || !rows.length) {
		return (
			<Card>
				<CardHeader>
					<CardHeading>{t("Detailed cost analysis")}</CardHeading>
				</CardHeader>
				<EmptyCardBody>
					<Empty>{t("No data is available.")}</Empty>
				</EmptyCardBody>
			</Card>
		);
	}

	return (
		<Card>
			<CardHeader>
				<CardHeading>{t("Detailed cost analysis")}</CardHeading>
			</CardHeader>
			<TableCardBody $columnCount={columns.length}>
				<Cell $isFirstCell={true} />
				{columns.map((column) => (
					<BoldCell key={column.isoCode}>{column.name}</BoldCell>
				))}
				{rows.map((row, rowIndex) => (
					<Fragment key={`row_${rowIndex}`}>
						<BoldCell $isFirstCell={true} $isLastRow={rowIndex === rows.length - 1}>
							{row.name}
						</BoldCell>
						{row.cells.map((cell, cellIndex) => (
							<Cell $isLastRow={rowIndex === rows.length - 1} key={`row_${rowIndex}_cell_${cellIndex}`}>
								<FormattedCurrency value={cell.costs} currencyType={cell.currency} />
							</Cell>
						))}
					</Fragment>
				))}
			</TableCardBody>
		</Card>
	);
}
